import { useEffect } from 'react'
import { datadogRum } from '@datadog/browser-rum'

const formatFullName = (firstName, lastName, fallback = '') => {
  const name = [firstName, lastName].filter(Boolean).join(' ')
  return name || fallback
}

const getUserId = user => user?.id || user?.userId

const buildDatadogUser = ({ id, name, email, portal }) => {
  if (!id) return null
  const user = { id, portal, email, name }
  return user
}

const safeClearUser = () => {
  if (typeof datadogRum.clearUser === 'function') {
    datadogRum.clearUser()
  } else {
    datadogRum.setUser({ id: null, portal: null, email: null, name: null })
  }
}

const setContext = context => {
  Object.entries(context).forEach(([key, value]) => {
    datadogRum.setGlobalContextProperty(key, value)
  })
}

// Function to sync the user's session with Datadog
export default function RumUserSync({
  isAuthenticated,
  isEmployeeUserLoggedInRatherThanCustomerUser,
  currentUser,
  currentUserEmployee,
}) {
  useEffect(() => {
    if (!isAuthenticated) {
      safeClearUser()
      setContext({
        portal: null,
        operatingAsCustomerData: null,
      })
      return
    }

    // EMPLOYEE SESSION
    if (isEmployeeUserLoggedInRatherThanCustomerUser) {
      const datadogUser = buildDatadogUser({
        id: currentUserEmployee?.userId,
        name: formatFullName(
          currentUserEmployee?.firstName,
          currentUserEmployee?.lastName,
          'Unknown'
        ),
        email: currentUserEmployee?.emailAddress,
        portal: 'employee',
      })

      if (datadogUser) {
        datadogRum.setUser(datadogUser)

        if (currentUser?.id || currentUser?.userId) {
          setContext({
            operatingAsCustomerData: {
              portal: 'employee operating as customer',
              operatingAsCustomerId: getUserId(currentUser) || null,
              operatingAsCustomerName: formatFullName(
                currentUser?.firstName,
                currentUser?.lastName,
                'Unknown'
              ),
              operatingAsCustomerEmail: currentUser?.emailAddress,
            },
          })
        } else {
          setContext({
            operatingAsCustomerData: null,
          })
        }
      }

      return
    }

    // CUSTOMER SESSION
    const datadogUser = buildDatadogUser({
      id: getUserId(currentUser),
      name: formatFullName(currentUser?.firstName, currentUser?.lastName, 'Unknown'),
      email: currentUser?.emailAddress,
      portal: 'customer',
    })

    if (datadogUser) {
      datadogRum.setUser(datadogUser)
      setContext({
        operatingAsCustomerData: null,
      })
    }
  }, [
    isAuthenticated,
    isEmployeeUserLoggedInRatherThanCustomerUser,
    currentUser,
    currentUserEmployee,
  ])

  return null
}
